import { customGeoJsonLayerConfig } from "@/constants/layerConfigAttributes";
import { ReservedValues } from "@/constants/reservedValues";
import {
  DefaultLightningDuration,
  DefaultLightningRenderMode,
  LightningDurationType,
  type LightningRenderMode,
  type LightningTypeFilterValue,
  getLightningDurationMin,
  getPreviousLightningInterval,
  isLayerVisible,
  isLightningDuration,
  isLightningRenderMode,
  lightningColorRange,
  lightningTypeFilterValues,
} from "@/geojson/definitions";
import { parseHexWithAlpha } from "@/layers/utility/color";
import { type AnyLayerWithoutId, MapboxLayerGroup } from "@/layers/utility/mapbox-utils";
import { thresholdColorExpressionFromProperties } from "@/layers/utility/thresholdColorExpression";
import type { ParameterUnit } from "@mm/api.meteomatics.com";
import type { CustomGeoJSONLayerOutput, LightningLayer } from "@mm/metx-workbench.meteomatics.com";
import { isEmpty } from "lodash";
import { type DateTime, Duration } from "luxon";
import type { ExpressionSpecification, FilterSpecification } from "mapbox-gl";

const LIGHTNING_UNKNOWN_CURRENT = Math.min(...ReservedValues);
const LIGHTNING_ICON_SIZE_RATIO = 0.01;
const LIGHTNING_HEATMAP_MIN_ZOOM = 0;
const LIGHTNING_HEATMAP_MAX_ZOOM = 8;
export const LIGHTNING_SYMBOL_MIN_ZOOM_WITH_HEATMAP = 8;
export const LIGHTNING_CLUSTER_LAYER_MAX_ZOOM = LIGHTNING_SYMBOL_MIN_ZOOM_WITH_HEATMAP;
export const LIGHTNING_CLUSTER_SOURCE_MAX_ZOOM = LIGHTNING_CLUSTER_LAYER_MAX_ZOOM - 1;
const LIGHTNING_HEATMAP_INTENSITY_MIN = 1;
const LIGHTNING_HEATMAP_INTENSITY_MAX = 2.1;
const LIGHTNING_HEATMAP_RADIUS_MIN = 5;
const LIGHTNING_HEATMAP_RADIUS_MAX = 12;
export const LIGHTNING_COLOR_BUCKET_PROPERTY = "lightning_color_bucket";
const LIGHTNING_CLOUD_TO_CLOUD_VALUE = 1;
const LIGHTNING_CLOUD_TO_GROUND_VALUE = 2;

const LIGHTNING_STROKE_CURRENT_EXPRESSION: ExpressionSpecification = [
  "to-number",
  ["get", "stroke_current"],
  LIGHTNING_UNKNOWN_CURRENT,
];
const LIGHTNING_TYPE_EXPRESSION: ExpressionSpecification = ["to-number", ["get", "lightning_type"], -999];
const LIGHTNING_RESERVED_CURRENT_EXPRESSION: ExpressionSpecification = [
  "in",
  LIGHTNING_STROKE_CURRENT_EXPRESSION,
  ["literal", ReservedValues],
];
const LIGHTNING_STROKE_TIME_MS_EXPRESSION: ExpressionSpecification = ["to-number", ["get", "stroke_time_ms"]];

const lightningPolarityIconExpression: ExpressionSpecification = [
  "case",
  LIGHTNING_RESERVED_CURRENT_EXPRESSION,
  "lightning-dot",
  [">", LIGHTNING_STROKE_CURRENT_EXPRESSION, 0],
  "lightning-cross",
  ["<", LIGHTNING_STROKE_CURRENT_EXPRESSION, 0],
  "lightning-minus",
  "lightning-dot",
];

/**
 * Converts UI text-size slider value to mapbox icon-size ratio.
 */
export const getLightningSymbolIconSize = (textSize: number): number => textSize * LIGHTNING_ICON_SIZE_RATIO;

const getLightningTypeFilterValues = (props: Pick<LightningLayer, "custom_options">): string[] | null => {
  const customOptions = props.custom_options;
  const filter = customOptions?.lightning_type_filter;
  if (!Array.isArray(filter)) {
    return null;
  }
  return filter;
};

const resolveLightningTypeFilterValue = (lightningType: unknown): LightningTypeFilterValue => {
  const parsed = Number(lightningType);
  if (parsed === LIGHTNING_CLOUD_TO_CLOUD_VALUE) {
    return "cloud_to_cloud";
  }
  if (parsed === LIGHTNING_CLOUD_TO_GROUND_VALUE) {
    return "cloud_to_ground";
  }
  return "unknown";
};

const isLightningTypeFilterValue = (value: string): value is LightningTypeFilterValue =>
  (lightningTypeFilterValues as readonly string[]).includes(value);

/**
 * Converts persisted values into a canonical filter list.
 * Returns `null` for "no filter" (including all-selected), so callers can treat it as show-all.
 */
export const normalizeLightningTypeFilter = (
  filter: string[] | null | undefined,
): LightningTypeFilterValue[] | null => {
  if (!filter || filter.length === 0) {
    return null;
  }

  const cleaned = [
    ...new Set(filter.filter((value): value is LightningTypeFilterValue => isLightningTypeFilterValue(value))),
  ];
  if (cleaned.length === 0 || cleaned.length === lightningTypeFilterValues.length) {
    return null;
  }

  return cleaned;
};

/**
 * Returns true when a lightning feature value matches the configured type filter.
 * Unknown filter/empty filter means "show all".
 */
export const matchesLightningTypeFilter = (filter: string[] | null | undefined, lightningType: unknown): boolean => {
  const normalizedFilter = normalizeLightningTypeFilter(filter);
  if (!normalizedFilter) {
    return true;
  }
  return normalizedFilter.includes(resolveLightningTypeFilterValue(lightningType));
};

/**
 * Builds a mapbox type-filter expression from persisted filter values.
 * Returns `null` when no explicit filtering should be applied.
 */
export const createLightningTypeFilterExpression = (
  filter: string[] | null | undefined,
): FilterSpecification | null => {
  const normalizedFilter = normalizeLightningTypeFilter(filter);
  if (!normalizedFilter) {
    return null;
  }

  const expressions = normalizedFilter.map((value) => {
    if (value === "cloud_to_cloud") {
      return ["==", LIGHTNING_TYPE_EXPRESSION, LIGHTNING_CLOUD_TO_CLOUD_VALUE];
    }
    if (value === "cloud_to_ground") {
      return ["==", LIGHTNING_TYPE_EXPRESSION, LIGHTNING_CLOUD_TO_GROUND_VALUE];
    }
    return [
      "all",
      ["!=", LIGHTNING_TYPE_EXPRESSION, LIGHTNING_CLOUD_TO_CLOUD_VALUE],
      ["!=", LIGHTNING_TYPE_EXPRESSION, LIGHTNING_CLOUD_TO_GROUND_VALUE],
    ];
  });

  if (expressions.length === 1) {
    return expressions[0] as FilterSpecification;
  }

  return ["any", ...expressions] as FilterSpecification;
};

export type LightningLayerName =
  | "heatmap-layer-1"
  | "heatmap-layer-2"
  | "heatmap-layer-3"
  | "heatmap-layer-4"
  | "heatmap-layer-5"
  | "heatmap-layer-6"
  | "heatmap-layer-7"
  | "cluster-circle-layer"
  | "cluster-count-layer"
  | "cluster-single-layer"
  | "timerange-layer";

export const lightningHeatmapLayerDefs = [
  { heatmap: "heatmap-layer-7", duration: LightningDurationType.LIGHTNINGS_720, color: lightningColorRange[6] },
  { heatmap: "heatmap-layer-6", duration: LightningDurationType.LIGHTNINGS_360, color: lightningColorRange[5] },
  { heatmap: "heatmap-layer-5", duration: LightningDurationType.LIGHTNINGS_180, color: lightningColorRange[4] },
  { heatmap: "heatmap-layer-4", duration: LightningDurationType.LIGHTNINGS_60, color: lightningColorRange[3] },
  { heatmap: "heatmap-layer-3", duration: LightningDurationType.LIGHTNINGS_30, color: lightningColorRange[2] },
  { heatmap: "heatmap-layer-2", duration: LightningDurationType.LIGHTNINGS_15, color: lightningColorRange[1] },
  { heatmap: "heatmap-layer-1", duration: LightningDurationType.LIGHTNINGS_1, color: lightningColorRange[0] },
] as const;

// Symbol color selection checks newest buckets first, so reverse heatmap order.
const lightningSymbolColorDefs = [...lightningHeatmapLayerDefs].reverse();
const lightningColorThresholdDurations = lightningSymbolColorDefs.map((entry) => entry.duration);

/**
 * Resolves render mode from layer custom options with a safe default.
 */
export const getLightningRenderMode = (props: Pick<LightningLayer, "custom_options">): LightningRenderMode => {
  const mode = props.custom_options?.clustering_type;
  if (isLightningRenderMode(mode)) {
    return mode;
  }
  return DefaultLightningRenderMode;
};

/**
 * Builds heatmap opacity that fades out at symbol zoom levels so heatmap and
 * symbols do not visually compete.
 */
export const buildLightningHeatmapOpacity = (opacity: number): ExpressionSpecification => {
  const baseOpacity = Math.max(0, Math.min(1, opacity));
  if (LIGHTNING_SYMBOL_MIN_ZOOM_WITH_HEATMAP >= LIGHTNING_HEATMAP_MAX_ZOOM) {
    return [
      "interpolate",
      ["linear"],
      ["zoom"],
      LIGHTNING_HEATMAP_MIN_ZOOM,
      baseOpacity,
      LIGHTNING_HEATMAP_MAX_ZOOM,
      baseOpacity,
    ];
  }
  return [
    "interpolate",
    ["linear"],
    ["zoom"],
    LIGHTNING_HEATMAP_MIN_ZOOM,
    baseOpacity,
    LIGHTNING_SYMBOL_MIN_ZOOM_WITH_HEATMAP,
    baseOpacity,
    LIGHTNING_HEATMAP_MAX_ZOOM,
    0,
  ];
};

/**
 * Returns the generated cluster property key for a lightning age bucket.
 */
export const getLightningClusterBucketPropertyName = (bucket: number): string => `lightning_bucket_count_${bucket}`;

/**
 * Declares per-bucket aggregate counters on mapbox cluster features.
 */
export const createLightningClusterProperties = (): Record<string, unknown> => {
  const clusterProperties: Record<string, unknown> = {};
  for (let i = 0; i < lightningColorRange.length; i++) {
    // Each cluster tracks per-bucket counts (`lightning_bucket_count_i`) that are
    // later used for cluster coloring and pie-marker composition.
    clusterProperties[getLightningClusterBucketPropertyName(i)] = [
      "+",
      ["case", ["==", ["to-number", ["get", LIGHTNING_COLOR_BUCKET_PROPERTY], -1], i], 1, 0],
    ];
  }
  return clusterProperties;
};

/**
 * Maps cluster bucket counters to a representative cluster color.
 */
export const createLightningClusterColorExpression = (): ExpressionSpecification => {
  const expression: unknown[] = ["case"];

  for (let i = 0; i < lightningColorRange.length; i++) {
    expression.push(
      [">", ["to-number", ["get", getLightningClusterBucketPropertyName(i)], 0], 0],
      lightningColorRange[i],
    );
  }

  expression.push(lightningColorRange[lightningColorRange.length - 1]);
  return expression as ExpressionSpecification;
};

/**
 * Maps precomputed feature bucket ids to point colors (cluster single points).
 */
export const createLightningBucketColorExpression = (): ExpressionSpecification => {
  const expression: unknown[] = ["match", ["to-number", ["get", LIGHTNING_COLOR_BUCKET_PROPERTY], 6]];
  for (let i = 0; i < lightningColorRange.length; i++) {
    expression.push(i, lightningColorRange[i]);
  }
  expression.push(lightningColorRange[lightningColorRange.length - 1]);
  return expression as ExpressionSpecification;
};

export const customGeoJSONMapboxLayerGroup = (
  instanceId: string,
  props: CustomGeoJSONLayerOutput,
  [pointSource, lineSource, polygonSource]: string[],
) => {
  const showValues = !!props.custom_options.show_values;
  const valueColor = props.custom_options.value_color || props.custom_options.line_color || "black";
  const valueTextSize = props.custom_options.value_text_size || customGeoJsonLayerConfig.defaultValueTextSize;
  return new MapboxLayerGroup(instanceId, [
    {
      layerName: "line-layer",
      type: "line",
      source: lineSource,
      paint: {
        "line-width": props.custom_options.line_width || customGeoJsonLayerConfig.defaultLineWidth,
        "line-opacity": props.opacity || 1,
        "line-color": props.custom_options.line_color || "black",
      },
      layout: {
        visibility: "visible",
        "line-join": "round",
        "line-cap": "round",
      },
    },

    {
      layerName: "polygon-layer",
      type: "fill",
      source: polygonSource,
      paint: {
        "fill-opacity": props.opacity || 1,
        "fill-color": props.custom_options.line_color || "black",
      },
      layout: {
        visibility:
          (!isEmpty(props.thresholds) || !isEmpty(props.advanced_thresholds)) && props.show ? "visible" : "none",
      },
    },
    {
      layerName: "polygon-layer-outline",
      type: "line",
      source: polygonSource,
      paint: {
        "line-width": props.custom_options.line_width || customGeoJsonLayerConfig.defaultLineWidth,
        "line-opacity": props.opacity || 1,
        "line-color": props.custom_options.line_color || "black",
      },
      layout: {
        visibility: props.show ? "visible" : "none",
      },
    },
    {
      layerName: "point-layer",
      type: "circle",
      source: pointSource,
      paint: {
        "circle-radius": 10,
        "circle-opacity": props.opacity || 1,
        "circle-color": [
          "case",
          ["has", "threshold_color"],
          thresholdColorExpressionFromProperties(props.custom_options.line_color || "black"),
          ["has", "ta_code"],
          ["case", ["has", "color"], ["get", "color"], "gray"],
          parseHexWithAlpha(props.custom_options.line_color || "gray"),
        ],
      },
      layout: {
        visibility: "visible",
        // Ensure higher-warning (lower number) severities draw on top of gray/others
        "circle-sort-key": ["case", ["has", "severity_level"], ["-", 100, ["to-number", ["get", "severity_level"]]], 0],
      },
    },
    {
      layerName: "line-value-layer",
      type: "symbol",
      source: lineSource,
      layout: {
        visibility: showValues && props.show ? "visible" : "none",
        "symbol-placement": "line",
        "text-field": ["to-string", ["coalesce", ["get", "value"], ""]],
        "text-size": valueTextSize,
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": valueColor,
        "text-halo-color": "#ffffff",
        "text-halo-width": 1,
      },
    },
    {
      layerName: "polygon-value-layer",
      type: "symbol",
      source: polygonSource,
      layout: {
        visibility: showValues && props.show ? "visible" : "none",
        "symbol-placement": "point",
        "text-field": ["to-string", ["coalesce", ["get", "value"], ""]],
        "text-size": valueTextSize,
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": valueColor,
        "text-halo-color": "#ffffff",
        "text-halo-width": 1,
      },
    },
  ]);
};

/**
 * Filters one non-overlapping age band used by stacked heatmap layers.
 */
export function createLightningTimerangeFilter(
  selectedTime: DateTime,
  param_unit: ParameterUnit,
  lightningTypeFilter?: string[] | null,
): FilterSpecification {
  const selectedTimeMs = selectedTime.valueOf();

  // Get the duration of the current interval and the previous interval
  const currentDurationMin = getLightningDurationMin(param_unit);
  const previousParamUnit = getPreviousLightningInterval(param_unit);
  const previousDurationMin = previousParamUnit ? getLightningDurationMin(previousParamUnit) : 0;

  // Calculate the bounds
  const upperBound = selectedTimeMs - Duration.fromObject({ minutes: previousDurationMin }).valueOf();
  const lowerBound = selectedTimeMs - Duration.fromObject({ minutes: currentDurationMin }).valueOf();

  const timerangeExpression: FilterSpecification = [
    "all",
    [">=", LIGHTNING_STROKE_TIME_MS_EXPRESSION, lowerBound],
    ["<", LIGHTNING_STROKE_TIME_MS_EXPRESSION, upperBound],
  ];
  const typeExpression = createLightningTypeFilterExpression(lightningTypeFilter);
  if (!typeExpression) {
    return timerangeExpression;
  }

  return ["all", ...timerangeExpression.slice(1), typeExpression];
}

/**
 * Filters the full selected lightning interval used by the symbol layer.
 */
export function createLightningSelectedRangeFilter(
  selectedTime: DateTime,
  param_unit: ParameterUnit,
  lightningTypeFilter?: string[] | null,
): FilterSpecification {
  const selectedTimeMs = selectedTime.valueOf();
  const currentDurationMin = getLightningDurationMin(param_unit);
  const lowerBound = selectedTimeMs - Duration.fromObject({ minutes: currentDurationMin }).valueOf();

  const timerangeExpression: FilterSpecification = [
    "all",
    [">=", LIGHTNING_STROKE_TIME_MS_EXPRESSION, lowerBound],
    ["<", LIGHTNING_STROKE_TIME_MS_EXPRESSION, selectedTimeMs],
  ];
  const typeExpression = createLightningTypeFilterExpression(lightningTypeFilter);
  if (!typeExpression) {
    return timerangeExpression;
  }

  return ["all", ...timerangeExpression.slice(1), typeExpression];
}

/**
 * Colors symbols by age relative to the selected time (newest bucket first).
 */
export const createLightningSymbolColorExpression = (selectedTime: DateTime): ExpressionSpecification => {
  const selectedTimeMs = selectedTime.valueOf();
  const expression: unknown[] = ["case"];

  for (const { duration, color } of lightningSymbolColorDefs) {
    const lowerBound = selectedTimeMs - Duration.fromObject({ minutes: getLightningDurationMin(duration) }).valueOf();
    expression.push([">=", LIGHTNING_STROKE_TIME_MS_EXPRESSION, lowerBound], color);
  }

  expression.push(lightningColorRange[lightningColorRange.length - 1]);
  return expression as ExpressionSpecification;
};

/**
 * Converts a lightning timestamp into its configured age bucket index.
 */
export const getLightningColorBucketIndex = (strokeTimeMs: number, selectedTime: DateTime): number => {
  if (!Number.isFinite(strokeTimeMs)) {
    return lightningColorRange.length - 1;
  }

  const selectedTimeMs = selectedTime.valueOf();
  for (let i = 0; i < lightningColorThresholdDurations.length; i++) {
    const durationMs = Duration.fromObject({
      minutes: getLightningDurationMin(lightningColorThresholdDurations[i]),
    }).valueOf();
    if (strokeTimeMs >= selectedTimeMs - durationMs) {
      // First matching bucket is the freshest interval (newest-first order).
      return i;
    }
  }

  return lightningColorRange.length - 1;
};

/**
 * Builds all lightning layers (heatmap/cluster/symbol) for the selected mode.
 */
export const lightningMapboxLayerGroup = (
  instanceId: string,
  props: LightningLayer,
  rawSource: string,
  clusteredSource: string,
  selectedTime: DateTime,
  parameter_unit: string,
): MapboxLayerGroup<LightningLayerName> => {
  const selectedDuration = isLightningDuration(parameter_unit) ? parameter_unit : DefaultLightningDuration;
  const renderMode = getLightningRenderMode(props);
  const lightningTypeFilter = getLightningTypeFilterValues(props);
  const heatmapEnabled = renderMode === "heatmap";
  const clusterModeEnabled = renderMode === "cluster";
  const symbolMinZoom = renderMode === "none" ? LIGHTNING_HEATMAP_MIN_ZOOM : LIGHTNING_SYMBOL_MIN_ZOOM_WITH_HEATMAP;

  const createHeatmapLayer = (
    layerName: Extract<LightningLayerName, `heatmap-layer-${1 | 2 | 3 | 4 | 5 | 6 | 7}`>,
    duration: LightningDurationType,
    color: string,
  ): AnyLayerWithoutId<LightningLayerName> => ({
    layerName,
    type: "heatmap",
    source: rawSource,
    minzoom: LIGHTNING_HEATMAP_MIN_ZOOM,
    maxzoom: LIGHTNING_HEATMAP_MAX_ZOOM,
    paint: {
      "heatmap-weight": 1,
      "heatmap-intensity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        LIGHTNING_HEATMAP_MIN_ZOOM,
        LIGHTNING_HEATMAP_INTENSITY_MIN,
        LIGHTNING_HEATMAP_MAX_ZOOM,
        LIGHTNING_HEATMAP_INTENSITY_MAX,
      ],
      "heatmap-radius": [
        "interpolate",
        ["linear"],
        ["zoom"],
        LIGHTNING_HEATMAP_MIN_ZOOM,
        LIGHTNING_HEATMAP_RADIUS_MIN,
        LIGHTNING_HEATMAP_MAX_ZOOM,
        LIGHTNING_HEATMAP_RADIUS_MAX,
      ],
      "heatmap-opacity": buildLightningHeatmapOpacity(props.opacity ?? 1),
      "heatmap-color": ["interpolate", ["linear"], ["heatmap-density"], 0, "rgba(0,0,0,0)", 1, color],
    },
    layout: {
      visibility: props.show && heatmapEnabled && isLayerVisible(selectedDuration, duration) ? "visible" : "none",
    },
    filter: createLightningTimerangeFilter(selectedTime, duration, lightningTypeFilter),
  });

  const createClusterCircleLayer = (): AnyLayerWithoutId<LightningLayerName> => ({
    layerName: "cluster-circle-layer",
    type: "circle",
    source: clusteredSource,
    minzoom: LIGHTNING_HEATMAP_MIN_ZOOM,
    maxzoom: LIGHTNING_CLUSTER_LAYER_MAX_ZOOM,
    filter: ["has", "point_count"],
    paint: {
      "circle-radius": [
        "interpolate",
        ["linear"],
        ["to-number", ["get", "point_count"]],
        1,
        12,
        20,
        18,
        50,
        24,
        100,
        30,
      ],
      "circle-color": createLightningClusterColorExpression(),
      "circle-opacity": props.opacity ?? 1,
      "circle-stroke-color": "#FFFFFF",
      "circle-stroke-opacity": props.opacity ?? 1,
      "circle-stroke-width": 2,
    },
    layout: {
      visibility: props.show && clusterModeEnabled ? "visible" : "none",
    },
  });

  const createClusterCountLayer = (): AnyLayerWithoutId<LightningLayerName> => ({
    layerName: "cluster-count-layer",
    type: "symbol",
    source: clusteredSource,
    minzoom: LIGHTNING_HEATMAP_MIN_ZOOM,
    maxzoom: LIGHTNING_CLUSTER_LAYER_MAX_ZOOM,
    filter: ["has", "point_count"],
    paint: {
      "text-color": "#111111",
      "text-opacity": props.opacity ?? 1,
    },
    layout: {
      "text-field": ["get", "point_count_abbreviated"],
      "text-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        LIGHTNING_HEATMAP_MIN_ZOOM,
        10,
        LIGHTNING_CLUSTER_LAYER_MAX_ZOOM,
        13,
      ],
      "text-allow-overlap": true,
      visibility: props.show && clusterModeEnabled ? "visible" : "none",
    },
  });

  const createClusterSingleLayer = (): AnyLayerWithoutId<LightningLayerName> => ({
    layerName: "cluster-single-layer",
    type: "circle",
    source: clusteredSource,
    minzoom: LIGHTNING_HEATMAP_MIN_ZOOM,
    maxzoom: LIGHTNING_SYMBOL_MIN_ZOOM_WITH_HEATMAP,
    filter: ["!", ["has", "point_count"]],
    paint: {
      "circle-radius": [
        "interpolate",
        ["linear"],
        ["zoom"],
        LIGHTNING_HEATMAP_MIN_ZOOM,
        2,
        LIGHTNING_CLUSTER_LAYER_MAX_ZOOM,
        5,
      ],
      "circle-color": createLightningBucketColorExpression(),
      "circle-opacity": props.opacity ?? 1,
      "circle-stroke-color": "#FFFFFF",
      "circle-stroke-opacity": props.opacity ?? 1,
      "circle-stroke-width": 0.5,
    },
    layout: {
      visibility: props.show && clusterModeEnabled ? "visible" : "none",
      "circle-sort-key": ["-", 100, ["to-number", ["get", LIGHTNING_COLOR_BUCKET_PROPERTY], 100]],
    },
  });

  const createSymbolLayer = (): AnyLayerWithoutId<LightningLayerName> => ({
    layerName: "timerange-layer",
    type: "symbol",
    source: rawSource,
    minzoom: symbolMinZoom,
    paint: {
      "icon-opacity": props.opacity,
      "icon-color": createLightningSymbolColorExpression(selectedTime),
    },
    layout: {
      "icon-image": lightningPolarityIconExpression,
      "icon-size": getLightningSymbolIconSize(props.text_size),
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      // Draw older lightning symbols first so the newest interval stays visually on top.
      "symbol-sort-key": LIGHTNING_STROKE_TIME_MS_EXPRESSION,
      visibility: props.show ? "visible" : "none",
    },
    filter: createLightningSelectedRangeFilter(selectedTime, selectedDuration, lightningTypeFilter),
  });

  return new MapboxLayerGroup(instanceId, [
    ...lightningHeatmapLayerDefs.map((layer) => createHeatmapLayer(layer.heatmap, layer.duration, layer.color)),
    createClusterCircleLayer(),
    createClusterCountLayer(),
    createClusterSingleLayer(),
    createSymbolLayer(),
  ]);
};
